import React, { Fragment, useState, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ArrowUpTrayIcon, DocumentArrowUpIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext.js';
import { getApiClient } from '../../api/apiClient';
import * as XLSX from 'xlsx';

const ImportParkingZonesModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      parseFile(selectedFile);
    }
  };

  // Parse Excel/CSV file
  const parseFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        
        // Transform data to match API format (NewParkingTariff DTO)
        const transformedData = jsonData.map(row => ({
          zoneName: row['Zone Name'] || row['zoneName'] || '',
          city: row['City'] || row['city'] || '',
          smsCode: row['SMS Code'] || row['smsCode'] || '',
          smsPhone: row['smsPhone'] || row['SMS Phone'] || '',
          ourPrice: parseFloat(row['OurPrice'] || row['Our Price'] || row['ourPrice'] || 0),
          ourCurrency: 'EUR',
          currency: 'RON',
          timeInMinutes: parseInt(row['Duration'] || row['timeInMinutes'] || 60),
          streets: (row['Streets'] || row['streets'] || '').split(';').map(s => s.trim()).filter(s => s)
        }));
        
        setPreviewData(transformedData);
        setShowPreview(true);
      } catch (error) {
        console.error('Error parsing file:', error);
        alert(t('errorParsingFile') + ': ' + error.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Handle import
  const handleImport = async () => {
    if (previewData.length === 0) {
      alert(t('noDataToImport'));
      return;
    }

    setImporting(true);
    setProgress(0);
    
    const results = {
      total: previewData.length,
      successful: 0,
      failed: 0,
      errors: []
    };

    try {
      const client = getApiClient();
      
      for (let i = 0; i < previewData.length; i++) {
        const zone = previewData[i];
        
        try {
          await client.post('/admin/parking/add-zone', zone);
          
          results.successful++;
        } catch (error) {
          results.failed++;
          results.errors.push({
            row: i + 1,
            zoneName: zone.zoneName,
            error: error.response?.data?.message || error.message
          });
        }
        
        // Update progress
        setProgress(Math.round(((i + 1) / previewData.length) * 100));
      }
      
      setImportResults(results);
      
      if (results.successful > 0) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error during import:', error);
      alert(t('errorDuringImport') + ': ' + error.message);
    } finally {
      setImporting(false);
    }
  };

  // Reset modal
  const handleClose = () => {
    setPreviewData([]);
    setShowPreview(false);
    setProgress(0);
    setImportResults(null);
    setImporting(false);
    onClose();
  };

  // Download template
  const handleDownloadTemplate = () => {
    const template = [
      {
        'Zone Name': 'Zona I',
        'City': 'Cluj-Napoca',
        'SMS Code': '403',
        'smsPhone': '7480',
        'OurPrice': '1.96',
        'Duration': '30',
        'Streets': 'Baba Novac; Bolyai; Emmanuel de Martonne'
      }
    ];
    
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Parking Zones');
    XLSX.writeFile(wb, 'parking_zones_template.xlsx');
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                    onClick={handleClose}
                    disabled={importing}
                  >
                    <span className="sr-only">{t('close')}</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ArrowUpTrayIcon className="h-6 w-6 text-purple-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                      {t('importParkingZones')}
                    </Dialog.Title>
                    
                    {!importResults ? (
                      <div className="mt-6 space-y-6">
                        {/* Instructions */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-blue-900 mb-2">{t('importInstructions')}</h4>
                          <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                            <li>{t('uploadExcelOrCSV')}</li>
                            <li>{t('requiredColumns')}: Zone Name, City, SMS Code, smsPhone, OurPrice, Duration, Streets</li>
                            <li>{t('streetsSeparatedBySemicolon')}</li>
                            <li>{t('durationInMinutes')}</li>
                            <li>{t('priceInEuro')}</li>
                          </ul>
                          <button
                            onClick={handleDownloadTemplate}
                            className="mt-3 text-xs text-purple-600 hover:text-purple-700 font-medium"
                          >
                            📥 {t('downloadTemplate')}
                          </button>
                        </div>

                        {/* File Upload */}
                        {!showPreview && (
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-400 transition-colors">
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept=".xlsx,.xls,.csv"
                              onChange={handleFileChange}
                              className="hidden"
                            />
                            <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="mt-4">
                              <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                              >
                                {t('selectFile')}
                              </button>
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                              {t('supportedFormats')}: .xlsx, .xls, .csv
                            </p>
                          </div>
                        )}

                        {/* Preview */}
                        {showPreview && !importing && (
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-sm font-medium text-gray-900">
                                {t('preview')} ({previewData.length} {t('zones')})
                              </h4>
                              <button
                                onClick={() => {
                                  setPreviewData([]);
                                  setShowPreview(false);
                                }}
                                className="text-sm text-gray-600 hover:text-gray-800"
                              >
                                {t('changeFile')}
                              </button>
                            </div>
                            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('zoneName')}</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('city')}</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('smsCode')}</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('smsPhone')}</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('price')}</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('duration')}</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('streets')}</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {previewData.map((zone, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                      <td className="px-3 py-2 text-sm text-gray-500">{idx + 1}</td>
                                      <td className="px-3 py-2 text-sm text-gray-900">{zone.zoneName}</td>
                                      <td className="px-3 py-2 text-sm text-gray-900">{zone.city}</td>
                                      <td className="px-3 py-2 text-sm text-gray-900">{zone.smsCode}</td>
                                      <td className="px-3 py-2 text-sm text-gray-900">{zone.smsPhone}</td>
                                      <td className="px-3 py-2 text-sm text-gray-900">{zone.ourPrice} {zone.ourCurrency}</td>
                                      <td className="px-3 py-2 text-sm text-gray-900">{zone.timeInMinutes} min</td>
                                      <td className="px-3 py-2 text-sm text-gray-500">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                          {zone.streets.length} {t('streets')}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Progress */}
                        {importing && (
                          <div className="space-y-4">
                            <div>
                              <div className="flex justify-between text-sm mb-2">
                                <span className="text-gray-700">{t('importing')}...</span>
                                <span className="font-medium text-purple-600">{progress}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                <div 
                                  className="bg-purple-600 h-3 rounded-full transition-all duration-300 ease-out"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                            </div>
                            <p className="text-sm text-gray-500 text-center">
                              {t('pleaseWaitWhileImporting')}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Results */
                      <div className="mt-6 space-y-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">{t('importResults')}</h4>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="text-center">
                              <div className="text-2xl font-bold text-gray-900">{importResults.total}</div>
                              <div className="text-xs text-gray-500">{t('total')}</div>
                            </div>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-green-600">{importResults.successful}</div>
                              <div className="text-xs text-gray-500">{t('successful')}</div>
                            </div>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-red-600">{importResults.failed}</div>
                              <div className="text-xs text-gray-500">{t('failed')}</div>
                            </div>
                          </div>
                        </div>

                        {importResults.successful > 0 && (
                          <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
                            <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                            <span className="text-sm text-green-800">
                              {importResults.successful} {t('zonesImportedSuccessfully')}
                            </span>
                          </div>
                        )}

                        {importResults.errors.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center text-sm font-medium text-red-900">
                              <ExclamationCircleIcon className="h-5 w-5 mr-2" />
                              {t('errors')} ({importResults.errors.length})
                            </div>
                            <div className="max-h-48 overflow-y-auto border border-red-200 rounded-lg bg-red-50 p-3">
                              {importResults.errors.map((err, idx) => (
                                <div key={idx} className="text-xs text-red-800 mb-2">
                                  <span className="font-medium">Row {err.row} ({err.zoneName}):</span> {err.error}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="mt-6 sm:flex sm:flex-row-reverse gap-3">
                      {!importResults && showPreview && !importing && (
                        <button
                          type="button"
                          onClick={handleImport}
                          disabled={importing || previewData.length === 0}
                          className="inline-flex w-full justify-center rounded-md bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t('startImport')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleClose}
                        disabled={importing}
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {importResults ? t('close') : t('cancel')}
                      </button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ImportParkingZonesModal;
